import { ColDef, ColGroupDef, HeaderClassParams, IServerSideDatasource, ValueFormatterParams } from "ag-grid-community";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import AgGridCustomTable from "../../../../../../../../../util/component/AgGridCustomTable";
import PinnedRowRender from "../../_components/pinned-row-render/PinnedRowRender";
import DashboardHeaderCol from "../../_components/dashboard-header-column/DashboardHeaderCol";
import { pendingBookingsByLocationFilterState } from "../../../../../recoil";
import { ISortBy } from "../../../../../../../api/types";
import { DEFAULT_TIME, IFormValuesMsActivitiesByRegionListDashboardFilter, IPendingBookingsByLocationsList, ITotalPendingBookingsByLocations } from "../../../../../types";
import { ReactQueryKey } from "../../../../../../../types";
import { getPendingBookingsByLocations, getTotalPendingBookingsByLocations } from "../../../../../../../api/services";
import "../../../../../dashboard.css";
import CustomLoader from "../../../../../../../../../util/component/CustomLoader";

/**
 * Table component of pending bookings by location
 */
const TablePendingBksByLoc = () => {

    const {t} = useTranslation('booking');

    const pendingListFilter = useRecoilValue(pendingBookingsByLocationFilterState);

    const queryClient = useQueryClient();

    const [pinnedBottomRowData, setPinnedBottomRowData] = useState<ITotalPendingBookingsByLocations | undefined>(undefined);

    const pendingBookingsByLocationsListMutation = useMutation(getPendingBookingsByLocations);
    const pendingBookingsByLocationsSummaryMutation = useMutation(getTotalPendingBookingsByLocations);

    // Column Definition
    const columnDefs = useMemo<ColDef<IPendingBookingsByLocationsList>[]>(() => [
        {
            field: 'location',
            headerName: t('label.pendingBookingsByLocationLocationColumn'),
            minWidth: 110,
            suppressColumnsToolPanel: true,
            lockPosition: "left",
            resizable: true
        },
        {
            headerName: t('label.pendingBookingsByLocationTotalGroupColumn'),
            marryChildren: true,
            groupId: 'GroupTotal',
            children: [
                {
                    field: 'toBeWorked',
                    headerName: t('label.pendingBookingsByLocationToBeWorkedColumn'),
                    suppressColumnsToolPanel: true,
                    headerTooltip: t("label.pendingBookingsByLocationToBeWorkedColumnTooltip"),
                    flex: 1,
                    resizable: true
                },
                {
                    field: 'maxIdleTime',
                    headerName: t('label.pendingBookingsByLocationMaxIdleTimeColumn'),
                    suppressColumnsToolPanel: true,
                    flex: 1,
                    minWidth: 120,
                    resizable: true,
                    valueFormatter: (params: ValueFormatterParams<IPendingBookingsByLocationsList>) => {
                        return !!params.data?.maxIdleTime ? params.data?.maxIdleTime : DEFAULT_TIME;
                    }
                }
            ]
        },
        {
            headerName: t('label.pendingBookingsByLocationTodayGroupColumn'),
            marryChildren: true,
            groupId: 'GroupToday',
            children: [
                {
                    field: 'confirmed',
                    headerName: t('label.pendingBookingsByLocationConfirmedColumn'),
                    suppressColumnsToolPanel: true,
                    flex: 1,
                    minWidth: 125,
                    resizable: true,
                },
                {
                    field: 'rejectedUnavailable',
                    headerName: t('label.pendingBookingsByLocationRejectedUnavailableColumn'),
                    suppressColumnsToolPanel: true,
                    flex: 1,
                    resizable: true,
                },
                {
                    field: 'worked',
                    headerName: t('label.pendingBookingsByLocationWorkedColumn'),
                    suppressColumnsToolPanel: true,
                    headerTooltip: t("label.pendingBookingsByLocationWorkedColumnTooltip"),
                    flex: 1,
                    resizable: true
                },
                {
                    field: 'avgWorkingTime',
                    headerName: t('label.pendingBookingsByLocationAvgWorkingTimeColumn'),
                    suppressColumnsToolPanel: true,
                    headerTooltip: t("label.pendingBookingsByLocationAvgWorkingTimeColumnTooltip"),
                    flex: 1,
                    minWidth: 157,
                    resizable: true,
                    valueFormatter: (params: ValueFormatterParams<IPendingBookingsByLocationsList>) => {
                        return !!params.data?.avgWorkingTime ? params.data?.avgWorkingTime : DEFAULT_TIME;
                    }
                },
                {
                    field: 'maxWorkingTime',
                    headerName: t('label.pendingBookingsByLocationMaxWorkingTimeColumn'),
                    suppressColumnsToolPanel: true,
                    headerTooltip: t("label.pendingBookingsByLocationMaxWorkingTimeColumnTooltip"),
                    flex: 1,
                    minWidth: 157,
                    resizable: true,
                    valueFormatter: (params: ValueFormatterParams<IPendingBookingsByLocationsList>) => {
                        return !!params.data?.maxWorkingTime ? params.data?.maxWorkingTime : DEFAULT_TIME;
                    }
                }
            ]
        }
    ], [t]);

    // defaultColGroupDef sets props common to all Group Columns
    const defaultColGroupDef = useMemo<Partial<ColGroupDef>>(() => {
        return {
            headerClass: headerClassFunc
        };
    }, [columnDefs]);

    // DefaultColDef sets props common to all Columns
    const defaultColDef = useMemo<ColDef>(() => ({
        resizable: false,
        unSortIcon: true,
        minWidth: 130,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        headerComponent: DashboardHeaderCol,
        cellRendererSelector: (params: any) => {
            if (params.node.rowPinned) {
                return {
                    component: PinnedRowRender,
                    params: {
                        field: ['location'],
                        style: { 'fontWeight': 'bold' },
                    }
                };
            } else {
                // rows that are not pinned don't use any cell renderer
                return undefined;
            }
        }
    }), []);

    // set class for Group Column
    function headerClassFunc(params: HeaderClassParams) {
        let cssClass = 'dashboard-column-group-no-view-resize'
        const colDef = params?.columnGroup?.getDefinition() as ColGroupDef;
        if (colDef?.groupId === 'GroupTotal' || colDef?.groupId === 'GroupToday') {
            cssClass += ' dashboard-column-group';
        }
        return cssClass;
    }

    // load table record
    const serverSideDatasource = useMemo<IServerSideDatasource>(() => ({
        getRows: async (params) => {
            try {
                let pendingBookingsByLocationsSummary = {} as ITotalPendingBookingsByLocations;

                const sortBy: ISortBy | undefined = params.request.sortModel[0] ? {
                    field: params.request.sortModel[0].colId,
                    order: params.request.sortModel[0].sort
                } : undefined;
                const start = params.request.startRow;
                const limit = (params.request.endRow !== undefined && params.request.startRow !== undefined) ? (params.request.endRow - params.request.startRow) : undefined;
                // get total for header
                queryClient.invalidateQueries({
                    queryKey: [ReactQueryKey.TotalHeaderDashboard, { tenants: pendingListFilter.tenants, dealers: pendingListFilter.dealers }]
                });

                // count for pagination and totals pinned row
                pendingBookingsByLocationsSummaryMutation.mutateAsync({
                    filters: pendingListFilter as IFormValuesMsActivitiesByRegionListDashboardFilter
                }).then((response: ITotalPendingBookingsByLocations) => {
                    if (!response.avgWorkingTime) {
                        response.avgWorkingTime = DEFAULT_TIME;
                    }
                    if (!response.maxWorkingTime) {
                        response.maxWorkingTime = DEFAULT_TIME;
                    }
                    //  set total for pinned row
                    pendingBookingsByLocationsSummary = response;
                    //  set total for pinned row
                    setPinnedBottomRowData(pendingBookingsByLocationsSummary);

                    // get records
                    pendingBookingsByLocationsListMutation.mutateAsync({
                        filters: pendingListFilter as IFormValuesMsActivitiesByRegionListDashboardFilter,
                        sortBy: sortBy,
                        start: start,
                        limit: limit
                    }).then((response: IPendingBookingsByLocationsList[]) => {
                        params.success({
                            rowData: response,
                            rowCount: pendingBookingsByLocationsSummary.totalRecords ?? 0
                        });

                        pendingBookingsByLocationsSummary.totalRecords == 0 && params.api.showNoRowsOverlay();
                    })
                        .catch((err: AxiosError) => {
                            params.fail();
                            params.api.hideOverlay();
                        });
                });
            }
            catch {
                params.fail();
                params.api.hideOverlay();
            }
        }
    }), [pendingListFilter]);

    return (
        <>
            <AgGridCustomTable
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                serverSideDatasource= {serverSideDatasource}
                paginationPageSize={25}
                rowModelType="serverSide"
                pinnedBottomRowData={[pinnedBottomRowData]}
                defaultColGroupDef={defaultColGroupDef}
                suppressMultiSort
                suppressDragLeaveHidesColumns
                cacheBlockSize={25}
                enableCellTextSelection
                tooltipShowDelay={0}
            />
            <CustomLoader
                loading={pendingBookingsByLocationsListMutation.isLoading ||
                pendingBookingsByLocationsSummaryMutation.isLoading} />
    </>
    );
}

export default TablePendingBksByLoc;
